// Supports weights 100-900
import { TypographyVariant } from '@/components/atoms/Typography/types';
import '@fontsource-variable/public-sans';
// do this instead if italic is needed:
// import '@fontsource-variable/public-sans/wght-italic.css';

function pxToRem(value: number) {
  return `${value / 16}rem`;
}

function responsiveFontSizes({ sm, md, lg }: { sm: number; md: number; lg: number }) {
  return {
    '@media (min-width:600px)': {
      fontSize: pxToRem(sm),
    },
    '@media (min-width:900px)': {
      fontSize: pxToRem(md),
    },
    '@media (min-width:1200px)': {
      fontSize: pxToRem(lg),
    },
  };
}

const FONT_PRIMARY = 'Public Sans Variable, sans-serif';

type VariantPixelValues = {
  fontSize: number;
  lineHeight: number;
  fontWeight: number;
  textTransform?: 'uppercase';
};

/**
 * Raw pixel values for typography styles.
 * Single source of truth - used for overflow detection and converted to rem for sx props.
 */
const variantConfig: Record<TypographyVariant, VariantPixelValues> = {
  h1: { fontSize: 40, lineHeight: 80, fontWeight: 800 },
  h2: { fontSize: 32, lineHeight: 64, fontWeight: 800 },
  h3: { fontSize: 24, lineHeight: 48, fontWeight: 700 },
  h4: { fontSize: 20, lineHeight: 36, fontWeight: 700 },
  h5: { fontSize: 18, lineHeight: 30, fontWeight: 700 },
  h6: { fontSize: 17, lineHeight: 28, fontWeight: 700 },
  subtitle1: { fontSize: 16, lineHeight: 24, fontWeight: 700 },
  subtitle2: { fontSize: 14, lineHeight: 22, fontWeight: 700 },
  body1: { fontSize: 16, lineHeight: 24, fontWeight: 400 },
  body2: { fontSize: 14, lineHeight: 22, fontWeight: 400 },
  caption: { fontSize: 12, lineHeight: 18, fontWeight: 400 },
  overline: { fontSize: 12, lineHeight: 18, fontWeight: 700, textTransform: 'uppercase' },
};

type VariantStyles = {
  fontSize: string;
  fontWeight: number;
  textTransform?: 'uppercase';
};

/**
 * Typography variant styles with rem values for use in sx props.
 */
export const variants = Object.fromEntries(
  Object.entries(variantConfig).map(([key, { fontSize, fontWeight, textTransform }]) => [
    key,
    {
      fontSize: pxToRem(fontSize),
      fontWeight,
      ...(textTransform && { textTransform }),
    },
  ]),
) as Record<TypographyVariant, VariantStyles>;

const typography = {
  fontFamily: FONT_PRIMARY,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  h1: {
    ...variants.h1,
    lineHeight: 80 / 64,
    ...responsiveFontSizes({ sm: 52, md: 58, lg: 64 }),
  },
  h2: {
    ...variants.h2,
    lineHeight: 64 / 48,
    ...responsiveFontSizes({ sm: 40, md: 44, lg: 48 }),
  },
  h3: {
    ...variants.h3,
    lineHeight: 1.5,
    ...responsiveFontSizes({ sm: 26, md: 30, lg: 32 }),
  },
  h4: {
    ...variants.h4,
    lineHeight: 1.5,
    ...responsiveFontSizes({ sm: 20, md: 24, lg: 24 }),
  },
  h5: {
    ...variants.h5,
    lineHeight: 1.5,
    ...responsiveFontSizes({ sm: 19, md: 20, lg: 20 }),
  },
  h6: {
    ...variants.h6,
    lineHeight: 28 / 18,
    ...responsiveFontSizes({ sm: 18, md: 18, lg: 18 }),
  },
  subtitle1: {
    ...variants.subtitle1,
    lineHeight: 1.5,
  },
  subtitle2: {
    ...variants.subtitle2,
    lineHeight: 22 / 14,
  },
  body1: {
    ...variants.body1,
    lineHeight: 1.5,
  },
  body2: {
    ...variants.body2,
    lineHeight: 22 / 14,
  },
  caption: {
    ...variants.caption,
    lineHeight: 1.5,
  },
  overline: {
    ...variants.overline,
    lineHeight: 1.5,
  },
  // Not part of TypographyVariant (excluded from design system components),
  // but required by MUI's Button and other components that reference theme.typography.button
  button: {
    fontWeight: 700,
    lineHeight: 24 / 14,
    fontSize: pxToRem(14),
    textTransform: 'capitalize',
  },
} as const;

export default typography;
